import { AgentBookingModel } from '@/__generated';
import { useCurrencyFormat } from '@/hooks/useCurrencyFormat';
import React from 'react';
import HotelJapaneseName from '../Hotels/HotelJapaneseName';
import HotelStar from '../Hotels/HotelStar';
import SingleOrPluralText from '../SingleOrPluralText';
import moment from 'moment';
import DefaultCancellationPolicy from '@/pages/HotelDetail/components/DefaultCancellationPolicy';
import { HOTEL_CONFIG, appDisplayDateFormat } from '@/config';
import { sumBy } from 'lodash';
import { HotelRoomStatus } from '@/constants';
import PlanTaxAndFee from '@/pages/HotelDetail/components/PlanTaxAndFee';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UncontrolledTooltip } from 'reactstrap';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import HotelRewardRating from '../HotelRewardRating';

type Props = {
  booking: AgentBookingModel;
  page: 'success' | 'failed' | 'cancel-success' | 'cancel-failed';
};

export default function HotelBookingSummary({ booking, page }: Props) {
  const { formatCurrency } = useCurrencyFormat();

  const numberOfNight = React.useMemo(() => {
    return moment(booking?.checkoutDate).diff(moment(booking?.checkinDate), 'day');
  }, [booking]);

  const rooms = React.useMemo(() => {
    if (page === 'success') {
      return booking.hotel?.rooms?.filter(x => x.status === HotelRoomStatus.Booked);
    }
    if (page === 'failed') {
      return booking.hotel?.rooms?.filter(x => x.status === HotelRoomStatus.Failed);
    }
    if (page === 'cancel-success') {
      return booking.hotel?.rooms?.filter(x => x.status === HotelRoomStatus.Cancelled);
    }
    return booking.hotel?.rooms;
  }, [booking, page]);
  const mainImage = React.useMemo(() => {
    let images = booking?.hotel?.images || [];
    if (images?.length) {
      return images[0];
    }
    return HOTEL_CONFIG.defaultImage;
  }, [booking]);
  return (
    <div className="px-30 py-30 border-light rounded-4">
      <div className="row x-gap-10">
        <div className="text-20 fw-500 mb-20 col">Your booking details</div>
      </div>
      <div className="row x-gap-15 y-gap-20 mb-10">
        <div className="col-auto">
          <img className="size-140 rounded-4 object-cover" src={mainImage} />
        </div>
        <div className="col">
          <div className="row">
            <div className="lh-17 fw-500 col">
              {booking.hotel?.hotelName}
              <HotelJapaneseName name={booking.hotel?.hotelNameJp} addBefore="-" />
            </div>
          </div>
          <div className="d-flex x-gap-5">
            <HotelStar star={booking.hotel?.starRating || 0} />
          </div>
          <div className="text-14 lh-15 mt-5">{booking.hotel?.address}</div>
          <div className="d-flex items-center"></div>
        </div>
      </div>
      <div className="border-top-light mt-10 mb-20" />
      <div className="row y-gap-20 justify-between">
        <div className="col-auto">
          <div className="text-15">Check-in</div>
          <div className="fw-500">{appDisplayDateFormat.default.dateOnly(booking.checkinDate)}</div>
          {booking.hotel?.checkinTime ? <div className="text-15 text-light-1">{booking.hotel?.checkinTime}</div> : null}
        </div>
        <div className="col-auto md:d-none">
          <div className="h-full w-1 bg-border" />
        </div>
        <div className="col-auto text-right md:text-left">
          <div className="text-15">Check-out</div>
          <div className="fw-500">{appDisplayDateFormat.default.dateOnly(booking.checkoutDate)}</div>
          {booking.hotel?.checkoutTime ? <div className="text-15 text-light-1">{booking.hotel?.checkoutTime}</div> : null}
        </div>
      </div>
      {/* End row */}

      <div className="border-top-light mt-10 mb-10" />
      <div>
        <div className="text-15">Total length of stay:</div>
        <div className="fw-500">
          <SingleOrPluralText count={numberOfNight} singleText="{} night" pluralText="{} nights" />
        </div>
      </div>

      <div className="border-top-light mt-10 mb-10" />
      <div className="text-15">You selected:</div>
      {rooms?.map((room, index) => {
        return (
          <div className="row  justify-between items-center" key={room.id}>
            <div className="col fw-500">{room.roomTypeName}</div>
            <div className="col-auto">
              <div className="text-15 fw-500">
                <SingleOrPluralText count={room.noOfRooms || 0} singleText="{} room" pluralText="{} rooms" />
              </div>
            </div>

            <div className="col-12">
              <div className="">
                <div className="text-14 fw-500 text-blue-1">
                  {[
                    `Check-in time: ${room.checkinTime || booking.hotel?.checkinTime}`,
                    `Check-out time: ${room.checkoutTime || booking.hotel?.checkinTime}`,
                  ].join(', ')}
                </div>
              </div>
              {room.remarks ? (
                <>
                  {room.supplier !== 'NTA' ? (
                    <div className="text-14 text-red-1 ">Additional charges may apply upon check in</div>
                  ) : (
                    <div className="text-14 text-red-1  items-center">
                      <b> Infant remarks:</b>
                      <div>{room.remarks}</div>
                    </div>
                  )}
                </>
              ) : null}
            </div>
            {room.planTitle ? (
              <div className={`text-14 fw-500  d-flex items-center text-green-2`}>
                <i className="icon-check text-12 mr-10" />
                {room.planTitle}
                {room.planDesc ? (
                  <div className="d-flex">
                    <FontAwesomeIcon icon={faCircleInfo} className="text-blue-1 font-16 ml-5" id={`room-planDesc-${index}`} />
                    <UncontrolledTooltip target={`room-planDesc-${index}`}>{room.planDesc}</UncontrolledTooltip>
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className="d-flex items-center text-green-2">
              <i className="icon-check text-12 mr-10" />
              <div className={`text-14 fw-500 d-flex items-center`}>
                <PlanTaxAndFee plan={room} />
                {room.paymentNotes ? (
                  <>
                    <FontAwesomeIcon icon={faCircleInfo} className="text-blue-1 font-16 ml-5" id={`room-paymentNotes-${index}`} />
                    <UncontrolledTooltip target={`room-paymentNotes-${index}`}>{room.paymentNotes}</UncontrolledTooltip>
                  </>
                ) : null}
              </div>
            </div>
            <div className="col-12 pb-10">
              <div className="border-bottom-light pb-10">
                <DefaultCancellationPolicy checkinDate={booking.checkinDate} plan={room as any} />
              </div>
            </div>
          </div>
        );
      })}

      {page === 'success' ? (
        <>
          <div className="text-15">Total Price</div>
          <div className="text-center">
            <span className="fw-500 text-22 text-blue-1">{formatCurrency(booking.sellingPrice)}</span>
          </div>

          <div className="text-15">Payment Due</div>
          <div className="text-center">
            <span className="fw-500 text-22 text-blue-1">{formatCurrency(booking.paymentDue)}</span>
          </div>
        </>
      ) : null}
      {page === 'failed' ? (
        <>
          <div className="text-15">Total Price</div>
          <div className="text-center">
            <span className="fw-500 text-22 text-blue-1">{formatCurrency(0)}</span>
          </div>
          <div className="text-15">Payment Due</div>
          <div className="text-center">
            <span className="fw-500 text-22 text-blue-1">{formatCurrency(0)}</span>
          </div>
        </>
      ) : null}
      {page === 'cancel-success' ? (
        <>
          <div className="text-15">Cancellation Fee</div>
          <div className="text-center">
            <span className="fw-500 text-22 text-blue-1">{formatCurrency(booking.sellingPrice)}</span>
          </div>
          <div className="text-15">Payment Due</div>
          <div className="text-center">
            <span className="fw-500 text-22 text-blue-1">{formatCurrency(booking.paymentDue)}</span>
          </div>
        </>
      ) : null}
      {page === 'cancel-failed' && !booking.onHold ? (
        <>
          <div className="text-15">Total Price</div>
          <div className="text-center">
            <span className="fw-500 text-22 text-blue-1">{formatCurrency(booking.sellingPrice)}</span>
          </div>
          <div className="text-15">Payment Due</div>
          <div className="text-center">
            <span className="fw-500 text-22 text-blue-1">{formatCurrency(booking.paymentDue)}</span>
          </div>
        </>
      ) : null}
    </div>
  );
}
